import { Rotate3d, Ruler } from 'lucide-react';
import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import { BoxZoomIcon, PanIcon } from '~/assets/workspace-icons';
import type { ToolEntry } from '~/components/toolbar/shared/toolEntry';
import { useMeasureMode } from '~/hooks/useMeasureMode';
import { pickedToolState, type Tools } from '~/state/atoms/toolsAtoms';

/** The tools that look at the image without changing it. */
export interface ViewingToolEntries {
    pan: ToolEntry;
    boxZoom: ToolEntry;
    rotateView: ToolEntry;
    /** The Measure tool, armed in the mode it was last used in. */
    measurement: ToolEntry;
}

/**
 * The tools that look at the image without changing it, as {@link ToolEntry}
 * rows. None is ever refused, and each arms at once, also while the open
 * operation previews or writes.
 */
export function useViewingToolEntries(): ViewingToolEntries {
    const intl = useIntl();
    const [tool, pickTool] = useRecoilState(pickedToolState);
    const measure = useMeasureMode();
    // Measuring has no off state of its own: armed, it stays in its mode.
    const resumeMeasuring = () => {
        if (measure.active === null) {
            measure.resume();
        }
    };

    const entry = (
        id: string,
        armedTool: Tools,
        labelId: string,
        icon: ToolEntry['icon'],
        hotkey: string
    ): ToolEntry => ({
        id,
        label: intl.formatMessage({ id: labelId }),
        icon,
        hotkey,
        selected: tool === armedTool,
        disabled: false,
        arm: () => pickTool(armedTool),
        toggle: () => pickTool(tool === armedTool ? 'pan' : armedTool),
    });

    return {
        pan: entry('pan', 'pan', 'toolbar.pan.tooltip', <PanIcon />, 'p'),
        boxZoom: entry(
            'box-zoom',
            'boxZoom',
            'toolbar.boxZoom.tooltip',
            <BoxZoomIcon />,
            'z'
        ),
        rotateView: entry(
            'rotate-view',
            'rotateView',
            'toolbar.rotateView.tooltip',
            <Rotate3d />,
            'o'
        ),
        measurement: {
            id: 'measurement',
            label: intl.formatMessage({ id: 'measure.tooltip' }),
            icon: <Ruler />,
            hotkey: 'm',
            selected: measure.active !== null,
            disabled: false,
            arm: resumeMeasuring,
            toggle: resumeMeasuring,
        },
    };
}
